import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  CHANGE_PLAN,
  SCHEDULE_DOWNGRADE,
  CANCEL_RECURRING_SUBSCRIPTION,
  REACTIVATE_RECURRING_SUBSCRIPTION,
  CREATE_TOKENIZATION_SESSION,
  CREATE_MONERIS_SUBSCRIPTION,
  UPGRADE_MONERIS_SUBSCRIPTION,
  DOWNGRADE_MONERIS_SUBSCRIPTION,
  CANCEL_MONERIS_SUBSCRIPTION,
  REACTIVATE_MONERIS_SUBSCRIPTION,
  START_SUBSCRIPTION,
  UPGRADE_MY_PLAN,
  SCHEDULE_MY_DOWNGRADE,
  CANCEL_MY_SUBSCRIPTION,
  REACTIVATE_MY_SUBSCRIPTION,
} from '../../graphql/billingQueries';
import type { PaymentProviderName } from '../../hooks/useActivePaymentProvider';
import { debugLog } from '../../utils/debugLog';
import { getEditorAccountErrorMessage } from '../../utils/editorAccount';
import { executeBillingLaunch, isStripeLaunch } from './BillingProviderLauncher';
import {
  FREE_PLAN_ID,
  OUTSTANDING_PAYMENT_NOTE,
  atPeriodEndPhrase,
  formatMonthlyPrice,
  formatPlanIdForDisplay,
  friendlyBillingError,
  isFreePlan,
  periodEndOrFallback,
  summarizeBilling,
  type BillingSummary,
} from './billingModel';
import type { BillingInfo, SubscriptionPlan } from './billingTypes';

export type NoticeSeverity = 'success' | 'info' | 'warning' | 'error';

export interface BillingNotice {
  message: string;
  severity: NoticeSeverity;
}

/** A plan change waiting for the customer's go-ahead (Stripe). */
export interface PendingPlanChange {
  kind: 'upgrade' | 'downgrade';
  planId: string;
  planName: string;
  price: number;
  currentPlanName: string;
}

export interface ConfirmRequest {
  title: string;
  body: string;
  confirmLabel: string;
  resolve: (confirmed: boolean) => void;
}

/** Shown on the payment-method page when a past-due subscriber tries to change plans. */
export const PAYMENT_RECOVERY_NOTICE =
  "There's a problem with the payment method on your subscription. Update your card before changing plans.";

const SETTINGS_LOADING = 'Still loading your billing settings. Try again in a moment.';
const SETTINGS_UNKNOWN =
  "We couldn't load your billing settings. Refresh the page and try again, or contact support if this keeps happening.";
const CHECKOUT_FAILED = "We couldn't open checkout. Please try again, or contact support if this keeps happening.";
const PLAN_CHANGE_FAILED = "We couldn't change your plan. Please try again, or contact support if this keeps happening.";
// Cancelling must always have a way through, so the fallback names one.
const CANCEL_FAILED =
  "We couldn't cancel your subscription. Please try again, or email info@artbits.solutions and we'll cancel it for you.";

interface UseBillingActionsArgs {
  user: { uid: string } | null | undefined;
  billing: BillingInfo | null | undefined;
  /** Sorted by price. */
  plans: SubscriptionPlan[];
  summary: BillingSummary;
  refetchBilling: () => Promise<{ data?: { getUserBilling?: BillingInfo | null } } | undefined | void>;
  refetchUsage: () => Promise<unknown>;
  refetchInvoices: () => Promise<unknown>;
  activePaymentProvider: PaymentProviderName | null;
  providerLoading: boolean;
}

/**
 * Every plan change, cancellation and reactivation on the billing page, with
 * the dialogs that confirm them. The provider forks are unchanged from the
 * original dashboard: Stripe runs through the provider-neutral mutations
 * (and fails closed while the provider is unknown); Moneris keeps its own
 * flow. Nothing that charges a card runs without an explicit confirmation.
 */
export function useBillingActions({
  user,
  billing,
  plans,
  summary,
  refetchBilling,
  refetchUsage,
  refetchInvoices,
  activePaymentProvider,
  providerLoading,
}: UseBillingActionsArgs) {
  const navigate = useNavigate();

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showPlanChange, setShowPlanChange] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [switchToFreeOpen, setSwitchToFreeOpen] = useState(false);
  const [planChangeConfirm, setPlanChangeConfirm] = useState<PendingPlanChange | null>(null);
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null);
  const [notice, setNotice] = useState<BillingNotice | null>(null);

  // Moneris tokenization (only mounted when the API reports Moneris).
  const [showTokenizationDialog, setShowTokenizationDialog] = useState(false);
  const [tokenizationUrl, setTokenizationUrl] = useState('');
  const [tokenizationTicket, setTokenizationTicket] = useState('');
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);

  // Refs stop a double click that lands before the loading flags re-render.
  const pickerInFlight = useRef(false);
  const confirmInFlight = useRef(false);
  const switchInFlight = useRef(false);
  const cancelInFlight = useRef(false);
  const keepInFlight = useRef(false);
  const createSubInFlight = useRef(false);

  const [changePlan, { loading: changePlanLoading }] = useMutation(CHANGE_PLAN);
  const [scheduleDowngrade] = useMutation(SCHEDULE_DOWNGRADE);
  const [cancelRecurringSubscription, { loading: cancelRecurringLoading }] = useMutation(CANCEL_RECURRING_SUBSCRIPTION);
  const [reactivateRecurringSubscription, { loading: reactivateRecurringLoading }] = useMutation(
    REACTIVATE_RECURRING_SUBSCRIPTION
  );
  const [createTokenizationSession, { loading: tokenizationLoading }] = useMutation(CREATE_TOKENIZATION_SESSION);
  const [createMonerisSubscription] = useMutation(CREATE_MONERIS_SUBSCRIPTION);
  const [upgradeMonerisSubscription, { loading: upgradeLoading }] = useMutation(UPGRADE_MONERIS_SUBSCRIPTION);
  const [downgradeMonerisSubscription, { loading: downgradeLoading }] = useMutation(DOWNGRADE_MONERIS_SUBSCRIPTION);
  const [cancelMonerisSubscription, { loading: cancelMonerisLoading }] = useMutation(CANCEL_MONERIS_SUBSCRIPTION);
  const [reactivateMonerisSubscription, { loading: reactivateMonerisLoading }] = useMutation(
    REACTIVATE_MONERIS_SUBSCRIPTION
  );
  const [startSubscription, { loading: startSubscriptionLoading }] = useMutation(START_SUBSCRIPTION);
  const [upgradeMyPlan, { loading: upgradeMyPlanLoading }] = useMutation(UPGRADE_MY_PLAN);
  const [scheduleMyDowngrade, { loading: scheduleMyDowngradeLoading }] = useMutation(SCHEDULE_MY_DOWNGRADE);
  const [cancelMySubscription, { loading: cancelMyLoading }] = useMutation(CANCEL_MY_SUBSCRIPTION);
  const [reactivateMySubscription, { loading: reactivateMyLoading }] = useMutation(REACTIVATE_MY_SUBSCRIPTION);

  // ---- Feedback -----------------------------------------------------------
  const notify = useCallback((message: string, severity?: NoticeSeverity) => {
    const clean = String(message ?? '').trim();
    const inferred: NoticeSeverity =
      severity ??
      (/(fail|could not|couldn't|error|unable|cannot|unexpected|missing|required)/i.test(clean)
        ? 'error'
        : /(still loading|already on this plan)/i.test(clean)
          ? 'info'
          : 'success');
    setNotice({ message: clean, severity: inferred });
  }, []);

  const clearNotice = useCallback(() => setNotice(null), []);

  /** Drops an error or warning (it belonged to a dialog the customer just closed); keeps a success. */
  const clearProblemNotice = useCallback(
    () => setNotice((current) => (current && (current.severity === 'error' || current.severity === 'warning') ? null : current)),
    []
  );

  const confirmAsync = useCallback(
    (title: string, body: string, confirmLabel = 'Continue') =>
      new Promise<boolean>((resolve) => setConfirmRequest({ title, body, confirmLabel, resolve })),
    []
  );

  const settleConfirm = (confirmed: boolean) => {
    confirmRequest?.resolve(confirmed);
    setConfirmRequest(null);
  };

  /** Refreshes after a change that already succeeded; a failed refresh must not read as a failed change. */
  const refreshQuietly = async (...refreshers: Array<() => Promise<unknown>>) => {
    try {
      await Promise.all(refreshers.map((refresh) => refresh()));
    } catch (error) {
      console.error('Billing refresh failed:', error);
    }
  };

  // The API's "This is an editor account" refusal, shown as is; otherwise the fallback.
  const messageFor = (error: unknown, fallback: string) => getEditorAccountErrorMessage(error) ?? fallback;

  // ---- Shared facts -------------------------------------------------------
  const currentPlanIsFree = summary.planIsFree;
  const currentPlanName = summary.planName;
  const atPeriodEnd = atPeriodEndPhrase(summary);
  const keepPlanLabel = currentPlanIsFree ? 'Keep subscription' : `Keep ${currentPlanName}`;
  const status = summary.status;

  // Stripe: a subscription exists provider-side regardless of whether it is
  // currently healthy. Sending a PAST_DUE / GRACE_PERIOD subscriber through
  // Checkout again would create a SECOND subscription and bill them twice,
  // so they fix their payment method instead.
  const stripeHasProviderSubscription = Boolean(billing?.providerSubscriptionId || billing?.subscriptionId);
  const stripeNeedsPaymentRecovery =
    stripeHasProviderSubscription && (billing?.status === 'PAST_DUE' || billing?.status === 'GRACE_PERIOD');
  const stripeHasActiveSubscription =
    stripeHasProviderSubscription && billing?.status === 'ACTIVE' && !billing?.cancelAtPeriodEnd;

  // ---- Plan picker --------------------------------------------------------
  const openPlanPicker = useCallback(() => {
    setSelectedPlan(summary.planId);
    setShowPlanChange(true);
  }, [summary.planId]);

  const planChangeBusy =
    changePlanLoading ||
    tokenizationLoading ||
    upgradeLoading ||
    downgradeLoading ||
    // Stripe: guard against double-submit on upgrade (would double-invoice via
    // proration_behavior=always_invoice), scheduleDowngrade and
    // startSubscription (a second Checkout session).
    startSubscriptionLoading ||
    upgradeMyPlanLoading ||
    scheduleMyDowngradeLoading;

  const closePlanPicker = () => {
    if (planChangeBusy) return;
    setShowPlanChange(false);
    setSelectedPlan(null);
    clearProblemNotice();
  };

  const selectPlan = (planId: string) => {
    setSelectedPlan(planId);
    clearProblemNotice();
  };

  /** What "Continue" in the picker will do, in words. */
  const continueLabel = (() => {
    const chosen = plans.find((plan) => plan.id === selectedPlan);
    if (!chosen || chosen.id === summary.planId || isFreePlan(chosen)) return 'Continue';
    if (activePaymentProvider === 'moneris') {
      // With a Moneris subscription on file, an upgrade charges the saved card
      // (after a confirmation) whatever the status, and anything else is
      // scheduled; without one, a payment form opens.
      if (billing?.monerisSubscriptionId) {
        return Number(chosen.price) > Number(summary.plan?.price ?? 0) ? 'Upgrade and pay now' : 'Schedule switch';
      }
      const monerisActive = Boolean(billing?.subscriptionId && billing?.status === 'ACTIVE' && !billing?.cancelAtPeriodEnd);
      return monerisActive ? 'Continue' : 'Continue to payment';
    }
    if (stripeNeedsPaymentRecovery) return 'Continue';
    return stripeHasActiveSubscription ? 'Continue' : 'Continue to checkout';
  })();

  const runPlanChange = async (planId: string) => {
    // A pending switch to Free (a cancellation at period end) blocks plan
    // changes until the customer keeps their plan.
    if (billing?.cancelAtPeriodEnd) {
      notify(`Your plan switches to Free ${atPeriodEnd}. ${keepPlanLabel} first, then choose a different plan.`, 'warning');
      setShowPlanChange(false);
      return;
    }

    // Free never goes through checkout. From a paid plan it ends the paid
    // subscription at period end, after an explicit confirmation.
    const chosenPlan = plans.find((plan) => plan.id === planId);
    if (isFreePlan(chosenPlan)) {
      if (currentPlanIsFree) {
        notify("You're already on the Free plan.", 'info');
        setShowPlanChange(false);
        setSelectedPlan(null);
        return;
      }
      setSwitchToFreeOpen(true);
      return;
    }

    // Only positively identified providers run; on any ambiguity (still
    // loading, query errored) fail closed rather than risk the wrong gateway.
    if (providerLoading) {
      notify(SETTINGS_LOADING, 'info');
      return;
    }
    if (!activePaymentProvider) {
      notify(SETTINGS_UNKNOWN, 'error');
      return;
    }

    const currentPlan = plans.find((plan) => plan.id === (billing?.planId || FREE_PLAN_ID));
    const newPlan = plans.find((plan) => plan.id === planId);
    const currentPrice = currentPlan?.price || 0;
    const newPrice = newPlan?.price || 0;

    if (activePaymentProvider === 'stripe') {
      try {
        if (planId === billing?.planId) {
          setShowPlanChange(false);
          setSelectedPlan(null);
          // With a switch scheduled, choosing the plan you're on keeps it.
          if (billing?.changePlanAtPeriodEnd) {
            const { data } = await scheduleMyDowngrade({ variables: { newPlanId: planId } });
            const result = data?.scheduleMyDowngrade;
            if (!result?.success) {
              notify(friendlyBillingError(result?.error, "The scheduled switch couldn't be cancelled. Please try again, or contact support."), 'error');
              return;
            }
            notify(result.message || `You'll stay on ${currentPlan?.name || 'your plan'}.`, 'success');
            await refreshQuietly(refetchBilling);
            return;
          }
          notify("You're already on this plan.", 'info');
          return;
        }

        if (stripeNeedsPaymentRecovery) {
          // Navigating unmounts this page, so the explanation travels with the route.
          setShowPlanChange(false);
          setSelectedPlan(null);
          navigate('/billing/payment-methods', { state: { notice: PAYMENT_RECOVERY_NOTICE } });
          return;
        }

        // No active subscription: Stripe Checkout. The customer pays on
        // Stripe's page; the webhook activates the plan on return.
        if (!stripeHasActiveSubscription) {
          if (newPrice <= 0) {
            notify("The Free plan doesn't need checkout.", 'info');
            return;
          }
          const { data } = await startSubscription({
            variables: { planId, currency: billing?.currency || 'CAD' },
          });
          const result = data?.startSubscription;
          if (!result?.success || !result.launch) {
            notify(messageFor({ message: result?.error }, friendlyBillingError(result?.error, CHECKOUT_FAILED)), 'error');
            return;
          }
          if (isStripeLaunch(result.launch)) {
            executeBillingLaunch(result.launch); // redirects; nothing runs after this
            return;
          }
          notify(CHECKOUT_FAILED, 'error');
          return;
        }

        // Active subscription: an upgrade (charges the card now) or a
        // downgrade (at period end). Both wait for the confirmation dialog.
        setPlanChangeConfirm({
          kind: newPrice > currentPrice ? 'upgrade' : 'downgrade',
          planId,
          planName: newPlan?.name || formatPlanIdForDisplay(planId),
          price: newPrice,
          currentPlanName: summary.isLegacyPaidBasic
            ? currentPlanName
            : currentPlan?.name || formatPlanIdForDisplay(billing?.planId || FREE_PLAN_ID),
        });
        return;
      } catch (error) {
        console.error('Stripe plan change error:', error);
        notify(messageFor(error, PLAN_CHANGE_FAILED), 'error');
        return;
      }
    }

    // Moneris (legacy) flow.
    try {
      const hasMonerisSubscription = Boolean(billing?.monerisSubscriptionId);
      const isUpgrade = newPrice > currentPrice;
      const isDowngrade = newPrice < currentPrice;

      if (planId === billing?.planId) {
        notify("You're already on this plan.", 'info');
        setShowPlanChange(false);
        setSelectedPlan(null);
        return;
      }

      const hasActiveSubscription = Boolean(
        (billing?.monerisSubscriptionId || billing?.subscriptionId) && billing?.status === 'ACTIVE' && !billing?.cancelAtPeriodEnd
      );
      // Payment for a new paid subscription, or an upgrade of an active one.
      // Never for a downgrade.
      const needsPayment = (!hasActiveSubscription && newPrice > 0) || (hasActiveSubscription && isUpgrade);
      debugLog('Moneris plan change:', { hasActiveSubscription, needsPayment, isUpgrade, isDowngrade });

      const openTokenization = async (): Promise<boolean> => {
        const { data } = await createTokenizationSession({
          variables: { userId: user?.uid, planId, amount: newPrice },
        });
        if (data?.createTokenizationSession?.success) {
          setPendingPlanId(planId);
          setTokenizationUrl(data.createTokenizationSession.tokenUrl);
          setTokenizationTicket(data.createTokenizationSession.ticketId);
          setShowTokenizationDialog(true);
          setShowPlanChange(false);
          return true;
        }
        console.error('Failed to create tokenization session:', data?.createTokenizationSession?.error);
        return false;
      };

      // Scheduled for the period end by Moneris; nothing is charged now.
      const runMonerisDowngrade = async () => {
        const { data } = await downgradeMonerisSubscription({
          variables: { input: { userId: user?.uid, newPlanId: planId } },
        });
        if (data?.downgradeMonerisSubscription?.success) {
          notify(data.downgradeMonerisSubscription.message || 'Plan downgrade scheduled', 'success');
          await refreshQuietly(refetchBilling);
          setShowPlanChange(false);
          setSelectedPlan(null);
          return;
        }
        notify(
          friendlyBillingError(data?.downgradeMonerisSubscription?.error, "The downgrade couldn't be scheduled. Please try again."),
          'error'
        );
      };

      if (needsPayment) {
        if (!hasMonerisSubscription) {
          if (!(await openTokenization())) notify("We couldn't open the payment form. Please try again.", 'error');
          return;
        }
        if (!isUpgrade) {
          await runMonerisDowngrade();
          return;
        }
        // This charges the saved card straight away, so ask first (as the
        // Stripe upgrade does).
        const upgradeName = newPlan?.name || formatPlanIdForDisplay(planId);
        const confirmedUpgrade = await confirmAsync(
          `Upgrade to ${upgradeName}?`,
          `${upgradeName} is ${formatMonthlyPrice(newPrice, summary.currency)} plus applicable tax. ` +
            'Your saved card is charged for the upgrade when you confirm.',
          'Upgrade and pay now'
        );
        if (!confirmedUpgrade) return;
        const { data } = await upgradeMonerisSubscription({
          variables: { input: { userId: user?.uid, newPlanId: planId } },
        });
        const result = data?.upgradeMonerisSubscription;
        if (result?.success) {
          notify(result.message || 'Plan upgraded', 'success');
          // An immediate upgrade changes the plan's limits, so usage is refetched too.
          await refreshQuietly(refetchBilling, refetchUsage, refetchInvoices);
          setShowPlanChange(false);
          return;
        }
        if (result?.code === 'NO_DATAKEY' || result?.code === 'NO_RECURRING_ID') {
          if (!(await openTokenization())) notify("We couldn't open the payment form. Please try again.", 'error');
          return;
        }
        notify("The upgrade didn't go through. Check your payment method, or contact support.", 'error');
        return;
      }

      if (hasMonerisSubscription && isDowngrade) {
        await runMonerisDowngrade();
        return;
      }

      const newPlanName = newPlan?.name || formatPlanIdForDisplay(planId);
      const currentName = currentPlan?.name || currentPlanName;
      const endDate = periodEndOrFallback(summary);
      if (isDowngrade) {
        const confirmed = await confirmAsync(
          `Downgrade to ${newPlanName}?`,
          `Your ${currentName} plan stays active until ${endDate}, then switches to ${newPlanName}. You keep every ` +
            `current feature until the end of this billing period, and you are not charged again for ${currentName}.`,
          'Schedule downgrade'
        );
        if (!confirmed) {
          setShowPlanChange(false);
          setSelectedPlan(null);
          return;
        }
        const { data } = await scheduleDowngrade({
          variables: {
            input: {
              userId: user?.uid || '',
              newPlanId: planId,
              effectiveDate: billing?.currentPeriodEnd || new Date().toISOString(),
            },
          },
        });
        if (data?.scheduleDowngrade && data.scheduleDowngrade.success === false) {
          notify(friendlyBillingError(data.scheduleDowngrade.error, "The downgrade couldn't be scheduled. Please try again."), 'error');
          return;
        }
        await refreshQuietly(refetchBilling);
        setShowPlanChange(false);
        setSelectedPlan(null);
        notify(`Downgrade scheduled. Your ${currentName} plan stays active until ${endDate}, then switches to ${newPlanName}.`, 'success');
        return;
      }

      // Not a downgrade and no payment needed: a direct plan change.
      const { data: changeData } = await changePlan({
        variables: { userId: user?.uid || '', newPlanId: planId },
      });
      const changeResult = changeData?.changePlan;
      if (changeResult && changeResult.success === false) {
        notify(friendlyBillingError(changeResult.error || changeResult.message, PLAN_CHANGE_FAILED), 'error');
        return;
      }
      // Plan limits changed, so usage is refetched too.
      await refreshQuietly(refetchBilling, refetchUsage);
      setShowPlanChange(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Plan change failed:', error);
      notify(messageFor(error, CHECKOUT_FAILED), 'error');
    }
  };

  const handlePlanChange = async () => {
    if (!selectedPlan || pickerInFlight.current) return;
    pickerInFlight.current = true;
    try {
      await runPlanChange(selectedPlan);
    } finally {
      pickerInFlight.current = false;
    }
  };

  // ---- Stripe upgrade / downgrade confirmation ------------------------------
  const confirmBusy = upgradeMyPlanLoading || scheduleMyDowngradeLoading;

  const confirmStripePlanChange = async () => {
    const pending = planChangeConfirm;
    if (!pending || confirmInFlight.current) return;
    confirmInFlight.current = true;
    try {
      if (pending.kind === 'upgrade') {
        const { data } = await upgradeMyPlan({ variables: { newPlanId: pending.planId } });
        const result = data?.upgradeMyPlan;
        if (!result?.success) {
          notify(
            friendlyBillingError(result?.error, "The upgrade didn't go through. Please try again, or contact support if this keeps happening."),
            'error'
          );
          return;
        }
        notify(result.message || `Upgraded to ${pending.planName}.`, 'success');
        // The upgrade changes the plan's limits (usage) and adds a prorated invoice.
        await refreshQuietly(refetchBilling, refetchUsage, refetchInvoices);
      } else {
        // Scheduled at period end: nothing is charged or refunded now.
        const { data } = await scheduleMyDowngrade({ variables: { newPlanId: pending.planId } });
        const result = data?.scheduleMyDowngrade;
        if (!result?.success) {
          notify(
            friendlyBillingError(result?.error, "The switch couldn't be scheduled. Please try again, or contact support if this keeps happening."),
            'error'
          );
          return;
        }
        notify(result.message || `Switch to ${pending.planName} scheduled for the end of your billing period.`, 'success');
        await refreshQuietly(refetchBilling);
      }
      setShowPlanChange(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Stripe plan change error:', error);
      notify(messageFor(error, PLAN_CHANGE_FAILED), 'error');
    } finally {
      confirmInFlight.current = false;
      setPlanChangeConfirm(null);
    }
  };

  const cancelPlanChangeConfirm = () => {
    if (confirmBusy) return;
    setPlanChangeConfirm(null);
  };

  // ---- Cancel -----------------------------------------------------------------
  const cancelBusy = cancelMonerisLoading || cancelRecurringLoading || cancelMyLoading;

  /** Cancels at period end. Resolves true when the subscription is cancelled. */
  const runCancel = async (): Promise<boolean> => {
    if (providerLoading) {
      notify(SETTINGS_LOADING, 'info');
      return false;
    }
    if (!activePaymentProvider) {
      notify(CANCEL_FAILED, 'error');
      return false;
    }

    const cancelledMessage = (endDate: string) =>
      summary.hasPaymentIssue
        ? currentPlanIsFree
          ? `Subscription cancelled. ${OUTSTANDING_PAYMENT_NOTE}`
          : `Subscription cancelled. You keep ${currentPlanName} until ${endDate}, then move to Free. ${OUTSTANDING_PAYMENT_NOTE}`
        : currentPlanIsFree
          ? "Subscription cancelled. You won't be charged again."
          : `Subscription cancelled. You keep ${currentPlanName} until ${endDate}, then move to Free. You won't be charged again.`;

    // The period end after the change, read from the refreshed record when
    // the refresh worked.
    const refreshedEndDate = async () => {
      try {
        const result = await refetchBilling();
        const refreshed = result && 'data' in result ? result.data?.getUserBilling : undefined;
        return refreshed ? periodEndOrFallback(summarizeBilling(refreshed, plans)) : periodEndOrFallback(summary);
      } catch (error) {
        console.error('Billing refresh failed:', error);
        return periodEndOrFallback(summary);
      }
    };

    if (activePaymentProvider === 'stripe') {
      // cancel_at_period_end on the Stripe side; the
      // customer.subscription.updated webhook reconciles shortly after.
      try {
        const { data } = await cancelMySubscription({ variables: { reason: 'User requested cancellation' } });
        const result = data?.cancelMySubscription;
        if (!result?.success) {
          // "Already cancelled / already scheduled": this page is out of date.
          if (/already/i.test(result?.error || '')) void refreshQuietly(refetchBilling);
          notify(friendlyBillingError(result?.error, CANCEL_FAILED), 'error');
          return false;
        }
        const endDate = await refreshedEndDate();
        setShowCancelDialog(false);
        notify(cancelledMessage(endDate), 'success');
        return true;
      } catch (error) {
        console.error('Stripe cancellation error:', error);
        notify(CANCEL_FAILED, 'error');
        return false;
      }
    }

    try {
      const hasMonerisSubscription = Boolean(billing?.monerisSubscriptionId);
      const result = hasMonerisSubscription
        ? await cancelMonerisSubscription({ variables: { userId: user?.uid || '' } })
        : await cancelRecurringSubscription({
            variables: {
              input: {
                userId: user?.uid || '',
                cancelImmediately: false,
                reason: 'User requested cancellation',
              },
            },
          });
      // Read the payload's own success flag: `result.data` is truthy even for { success: false }.
      const payload = hasMonerisSubscription
        ? result?.data?.cancelMonerisSubscription
        : result?.data?.cancelRecurringSubscription;
      if (!payload?.success) {
        notify(friendlyBillingError(payload?.error || payload?.message, CANCEL_FAILED), 'error');
        return false;
      }
      const endDate = await refreshedEndDate();
      setShowCancelDialog(false);
      notify(cancelledMessage(endDate), 'success');
      return true;
    } catch (error) {
      console.error('Cancellation failed:', error);
      notify(CANCEL_FAILED, 'error');
      return false;
    }
  };

  const handleCancelSubscription = async (): Promise<boolean> => {
    if (cancelInFlight.current) return false;
    cancelInFlight.current = true;
    try {
      return await runCancel();
    } finally {
      cancelInFlight.current = false;
    }
  };

  const openCancelDialog = () => {
    clearProblemNotice();
    setShowCancelDialog(true);
  };

  const closeCancelDialog = () => {
    if (cancelBusy) return;
    setShowCancelDialog(false);
    clearProblemNotice();
  };

  // ---- Paid plan -> Free ------------------------------------------------------
  const switchBusy = scheduleMyDowngradeLoading || cancelBusy;

  // The API ends the paid subscription at period end: the customer keeps the
  // plan they paid for until then and is not charged again. A subscription
  // whose payment is failing (past due, grace period) can't be scheduled, but
  // can always be cancelled, which has the same outcome.
  const confirmSwitchToFree = async () => {
    if (switchInFlight.current) return;
    switchInFlight.current = true;
    try {
      if (status === 'PAST_DUE' || status === 'GRACE_PERIOD') {
        if (await handleCancelSubscription()) {
          setShowPlanChange(false);
          setSelectedPlan(null);
        }
        return;
      }
      const { data } = await scheduleMyDowngrade({ variables: { newPlanId: FREE_PLAN_ID } });
      const result = data?.scheduleMyDowngrade;
      if (!result?.success) {
        notify(
          friendlyBillingError(result?.error, "The switch to Free couldn't be scheduled. Please try again, or contact support if this keeps happening."),
          'error'
        );
        return;
      }
      await refreshQuietly(refetchBilling);
      notify(result.message || `Your plan will switch to Free ${atPeriodEnd}. You won't be charged again.`, 'success');
      setShowPlanChange(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Switch to Free error:', error);
      notify(
        messageFor(error, "The switch to Free couldn't be scheduled. Please try again, or contact support if this keeps happening."),
        'error'
      );
    } finally {
      switchInFlight.current = false;
      setSwitchToFreeOpen(false);
    }
  };

  const closeSwitchToFree = () => {
    if (switchBusy) return;
    setSwitchToFreeOpen(false);
  };

  // ---- Keep the plan (undo a cancellation) ------------------------------------
  const keepBusy = reactivateMonerisLoading || reactivateRecurringLoading || reactivateMyLoading;

  const handleKeepPlan = async () => {
    if (keepInFlight.current) return;
    const confirmed = await confirmAsync(
      `${keepPlanLabel}?`,
      currentPlanIsFree
        ? 'Your subscription continues and billing resumes on your next billing date.'
        : `${currentPlanName} continues and renews ${atPeriodEnd}, so you don't move to Free.`,
      keepPlanLabel
    );
    if (!confirmed) return;
    keepInFlight.current = true;
    const keepFailed = `We couldn't keep ${currentPlanIsFree ? 'your subscription' : currentPlanName}. Please try again, or contact support if this keeps happening.`;
    try {
      if (providerLoading) {
        notify(SETTINGS_LOADING, 'info');
        return;
      }
      if (!activePaymentProvider) {
        notify(SETTINGS_UNKNOWN, 'error');
        return;
      }
      if (activePaymentProvider === 'stripe') {
        // Clears cancel_at_period_end on the Stripe side; the webhook reconciles.
        try {
          const { data } = await reactivateMySubscription();
          const result = data?.reactivateMySubscription;
          if (!result?.success) {
            notify(friendlyBillingError(result?.error, keepFailed), 'error');
            return;
          }
          await refreshQuietly(refetchBilling);
          notify(
            currentPlanIsFree ? 'Your subscription continues.' : `You're keeping ${currentPlanName}. It renews ${atPeriodEnd}.`,
            'success'
          );
        } catch (error) {
          console.error('Stripe reactivation error:', error);
          notify(keepFailed, 'error');
        }
        return;
      }

      try {
        const hasMonerisSubscription = Boolean(billing?.monerisSubscriptionId);
        const result = hasMonerisSubscription
          ? await reactivateMonerisSubscription({ variables: { userId: user?.uid || '' } })
          : await reactivateRecurringSubscription({
              variables: { input: { userId: user?.uid || '', restorePreviousPlan: true } },
            });
        const payload = hasMonerisSubscription
          ? result?.data?.reactivateMonerisSubscription
          : result?.data?.reactivateRecurringSubscription;
        if (!payload?.success) {
          throw new Error(payload?.error || payload?.message || 'Failed to reactivate subscription');
        }
        let renewsOn = periodEndOrFallback(summary);
        try {
          const refreshed = await refetchBilling();
          const fresh = refreshed && 'data' in refreshed ? refreshed.data?.getUserBilling : undefined;
          if (fresh) renewsOn = periodEndOrFallback(summarizeBilling(fresh, plans));
        } catch (error) {
          console.error('Billing refresh failed:', error);
        }
        notify(
          currentPlanIsFree ? 'Your subscription continues.' : `You're keeping ${currentPlanName}. Next billing date ${renewsOn}.`,
          'success'
        );
      } catch (error: any) {
        console.error('Reactivation failed:', error);
        const needsPaymentMethod = error?.graphQLErrors?.some(
          (graphQLError: any) => graphQLError.extensions?.code === 'NO_DATAKEY' || error.message?.includes('NO_DATAKEY')
        );
        if (needsPaymentMethod) {
          if (
            await confirmAsync(
              'Update your payment method',
              'Your payment method needs updating before the subscription can be reactivated. Add one now?',
              'Update payment method'
            )
          ) {
            void handlePlanChange();
          }
        } else {
          notify(friendlyBillingError(error?.message, keepFailed), 'error');
        }
      }
    } finally {
      keepInFlight.current = false;
    }
  };

  // ---- Moneris tokenization -------------------------------------------------------
  const handleTokenizationComplete = async (ticket: string, dataKey?: string) => {
    if (createSubInFlight.current) return;
    createSubInFlight.current = true;
    setShowTokenizationDialog(false);

    if (!pendingPlanId || !user?.uid) {
      notify('Choose a plan to continue.', 'info');
      createSubInFlight.current = false;
      return;
    }

    try {
      // Pass the dataKey when we have one; otherwise the ticket, from which
      // the API extracts it.
      const input: Record<string, string> = { userId: user.uid, planId: pendingPlanId };
      if (dataKey) input.dataKey = dataKey;
      else input.ticket = ticket;

      const { data } = await createMonerisSubscription({ variables: { input } });
      const result = data?.createMonerisSubscription;
      if (result?.success) {
        // A plan change that was waiting for a card: retry the upgrade.
        const currentPrice = plans.find((plan) => plan.id === (billing?.planId || FREE_PLAN_ID))?.price || 0;
        const pendingPlan = plans.find((plan) => plan.id === pendingPlanId);
        const newPrice = pendingPlan?.price || 0;

        if (billing?.planId && newPrice > currentPrice) {
          try {
            const { data: upgradeData } = await upgradeMonerisSubscription({
              variables: { input: { userId: user.uid, newPlanId: pendingPlanId } },
            });
            if (upgradeData?.upgradeMonerisSubscription?.success) {
              notify('Payment method updated and plan upgraded', 'success');
            } else {
              notify("Payment method updated, but the upgrade didn't go through. Please try again.", 'error');
            }
          } catch (upgradeError) {
            console.error('Error retrying upgrade:', upgradeError);
            notify("Payment method updated, but the upgrade didn't go through. Please try again.", 'error');
          }
        } else {
          const planName = pendingPlan?.name || formatPlanIdForDisplay(pendingPlanId);
          notify(`${planName} plan activated. Your subscription renews monthly.`, 'success');
        }

        await refreshQuietly(refetchBilling, refetchUsage, refetchInvoices);
        setPendingPlanId(null);
        setSelectedPlan(null);
        setShowPlanChange(false);
      } else {
        console.error('Subscription activation failed:', result?.message);
        notify(
          "We couldn't activate your subscription. If you were charged, contact support and we'll sort it out.",
          'error'
        );
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      notify("Your payment went through, but we couldn't update your plan. Please contact support.", 'error');
    } finally {
      createSubInFlight.current = false;
    }
  };

  // Closing the payment form: check whether the payment completed anyway.
  const handleTokenizationCancel = async () => {
    setShowTokenizationDialog(false);
    if (pendingPlanId && tokenizationTicket && !createSubInFlight.current && user?.uid) {
      createSubInFlight.current = true;
      try {
        const { data } = await createMonerisSubscription({
          variables: { input: { userId: user.uid, planId: pendingPlanId, ticket: tokenizationTicket } },
        });
        if (data?.createMonerisSubscription?.success) {
          await refreshQuietly(refetchBilling, refetchUsage, refetchInvoices);
          notify('Payment confirmed and your subscription is active.', 'success');
          setSelectedPlan(null);
          setShowPlanChange(false);
        }
      } catch (error) {
        debugLog('Fallback payment verification found no completed payment:', error);
      } finally {
        setPendingPlanId(null);
        createSubInFlight.current = false;
      }
    } else {
      setPendingPlanId(null);
    }
  };

  return {
    // picker
    selectedPlan,
    showPlanChange,
    openPlanPicker,
    closePlanPicker,
    selectPlan,
    handlePlanChange,
    continueLabel,
    planChangeBusy,
    // Stripe confirmation
    planChangeConfirm,
    confirmStripePlanChange,
    cancelPlanChangeConfirm,
    confirmBusy,
    // switch to Free
    switchToFreeOpen,
    confirmSwitchToFree,
    closeSwitchToFree,
    switchBusy,
    // cancel
    showCancelDialog,
    openCancelDialog,
    closeCancelDialog,
    handleCancelSubscription,
    cancelBusy,
    // keep
    handleKeepPlan,
    keepBusy,
    keepPlanLabel,
    // generic confirmation
    confirmRequest,
    settleConfirm,
    // feedback
    notice,
    notify,
    clearNotice,
    // Moneris
    showTokenizationDialog,
    tokenizationUrl,
    tokenizationTicket,
    handleTokenizationComplete,
    handleTokenizationCancel,
  };
}

export type BillingActions = ReturnType<typeof useBillingActions>;
